import t from "@ext/localization/locale/translate";
import {
	getStandardCaseByLower,
	StandardCaseLangList,
} from "@ext/markdown/elements/codeBlockLowlight/edit/logic/LowlightLangs";
import type { Node } from "@tiptap/pm/model";
import {
	DropdownEmpty,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSearchItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	useSearchableMenu,
} from "@ui-kit/Dropdown";
import { GlassToolbarIcon, GlassToolbarText, GlassToolbarTriggerChevron } from "@ui-kit/GlassToolbar";
import { Icon } from "@ui-kit/Icon";
import { MenuItemText } from "@ui-kit/MenuItem";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useCallback, useMemo } from "react";

interface CodeBlockActionsProps {
	node: Node;
	onChange: (lang: string) => void;
}

const languages = StandardCaseLangList.map((label) => ({ label }));

const CodeBlockActions = ({ node, onChange }: CodeBlockActionsProps) => {
	const tooltip = t("language.name");
	const currentLanguage = getStandardCaseByLower(node.attrs.language) || "";
	const { search, setSearch, contentRef, inputRef, handleContentKeyDown, handleInputKeyDown, filterItems } =
		useSearchableMenu();
	const filteredLanguages = useMemo(() => filterItems(languages), [filterItems]);

	const onOpenChange = useCallback(
		(open: boolean) => {
			if (!open) setSearch("");
		},
		[setSearch],
	);

	return (
		<DropdownMenu onOpenChange={onOpenChange}>
			<Tooltip>
				<TooltipTrigger asChild>
					<DropdownMenuTrigger asChild>
						<GlassToolbarTriggerChevron aria-label={tooltip}>
							<GlassToolbarIcon icon="code-xml" />
							{currentLanguage && (
								<GlassToolbarText className="text-xs font-medium">{currentLanguage}</GlassToolbarText>
							)}
						</GlassToolbarTriggerChevron>
					</DropdownMenuTrigger>
				</TooltipTrigger>
				<TooltipContent sideOffset={2}>{tooltip}</TooltipContent>
			</Tooltip>
			<DropdownMenuContent
				align="start"
				className="w-[min(90dvw,12rem)] max-h-[min(45dvh,20rem)] overflow-y-auto"
				onKeyDown={handleContentKeyDown}
				ref={contentRef}
				side="top"
				sideOffset={8}
			>
				<DropdownMenuSearchItem
					onChange={(e) => setSearch(e.target.value)}
					onClick={(e) => e.stopPropagation()}
					onKeyDown={handleInputKeyDown}
					placeholder={t("search.placeholder")}
					ref={inputRef}
					value={search}
				/>
				<DropdownMenuSeparator />
				<ScrollShadowContainer className="max-h-44 flex-1 overflow-y-auto">
					{filteredLanguages.length === 0 ? (
						<DropdownEmpty>{t("list.no-results-found")}</DropdownEmpty>
					) : (
						filteredLanguages.map(({ label }) => (
							<DropdownMenuItem key={label} onSelect={() => onChange(label)} textValue={label}>
								<MenuItemText>{label}</MenuItemText>
								{currentLanguage === label && <Icon className="ml-auto" icon="check" />}
							</DropdownMenuItem>
						))
					)}
				</ScrollShadowContainer>
				{currentLanguage && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={() => onChange(null)}>
							<Icon icon="trash" />
							{t("clear")}
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default CodeBlockActions;
